import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useReducer,
  ReactNode,
} from 'react';

import { useLocation } from 'react-router';

export type SortDirection = 'asc' | 'desc';

/* --------------------------------------------------------------------------
 * Types & Interfaces
 * -------------------------------------------------------------------------- */

interface State {
  sort: {
    typeDirection: SortDirection | null;
    nameDirection: SortDirection | null;
    updatedDirection: SortDirection | null;
  };

  filters: { textFilter: string };
}

/* --------------------------------------------------------------------------
 * Actions & Reducer helpers
 * -------------------------------------------------------------------------- */

type Action =
  { type: 'TOGGLE_SORT'; key: keyof State['sort'] } | { type: 'SET_TEXT_FILTER'; value: string };

const resetMap: Record<keyof State['sort'], Array<keyof State['sort']>> = {
  typeDirection: ['nameDirection', 'updatedDirection'],
  nameDirection: ['typeDirection', 'updatedDirection'],
  updatedDirection: ['typeDirection', 'nameDirection'],
};

const initialState: State = {
  sort: { typeDirection: null, nameDirection: null, updatedDirection: null },
  filters: { textFilter: '' },
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'TOGGLE_SORT': {
      const key = action.key;
      const current = state.sort[key];
      const next: SortDirection | null =
        current === null ? 'asc' : current === 'asc' ? 'desc' : null;
      const sort = { ...state.sort, [key]: next } as State['sort'];
      resetMap[key].forEach((k) => (sort[k] = null));
      return { ...state, sort };
    }
    case 'SET_TEXT_FILTER':
      return { ...state, filters: { ...state.filters, textFilter: action.value } };
    default:
      return state;
  }
};

/* --------------------------------------------------------------------------
 * Context API
 * -------------------------------------------------------------------------- */

export interface InsightFilterContextType {
  typeDirection: SortDirection | null;
  nameDirection: SortDirection | null;
  updatedDirection: SortDirection | null;
  textFilter: string;
  setTextFilter: (t: string) => void;
  toggleTypeDirection: () => void;
  toggleNameDirection: () => void;
  toggleUpdatedDirection: () => void;
}

const InsightFilterContext = createContext<InsightFilterContextType | undefined>(undefined);

/* --------------------------------------------------------------------------
 * Provider component
 * -------------------------------------------------------------------------- */

interface ProviderProps {
  children: ReactNode;
}

/**
 * Provider component for insight filter context
 * @param children - Child components to wrap with the provider
 */
export const InsightFilterProvider: React.FC<ProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  /* sort toggles */
  const toggleTypeDirection = useCallback(
    () => dispatch({ type: 'TOGGLE_SORT', key: 'typeDirection' }),
    []
  );

  const toggleNameDirection = useCallback(
    () => dispatch({ type: 'TOGGLE_SORT', key: 'nameDirection' }),
    []
  );

  const toggleUpdatedDirection = useCallback(
    () => dispatch({ type: 'TOGGLE_SORT', key: 'updatedDirection' }),
    []
  );

  /* text filters */
  const setTextFilter = useCallback(
    (value: string) => dispatch({ type: 'SET_TEXT_FILTER', value }),
    []
  );

  /* context value */
  const value = useMemo<InsightFilterContextType>(
    () => ({
      ...state.sort,
      ...state.filters,
      setTextFilter,
      toggleTypeDirection,
      toggleNameDirection,
      toggleUpdatedDirection,
    }),
    [state, setTextFilter, toggleTypeDirection, toggleNameDirection, toggleUpdatedDirection]
  );

  return <InsightFilterContext.Provider value={value}>{children}</InsightFilterContext.Provider>;
};

/* --------------------------------------------------------------------------
 * Hook
 * -------------------------------------------------------------------------- */

/**
 * Hook to access insight filter context
 * @returns The insight filter context
 */
export const useInsightFilter = (): InsightFilterContextType => {
  const ctx = useContext(InsightFilterContext);
  const { pathname } = useLocation();

  if (!ctx && pathname.includes('/insights')) {
    console.error('useInsightFilter must be used within an InsightFilterProvider');
  }

  // fallback keeps existing callers happy on unrelated routes.
  // It won't ever be hit where the provider is mounted.
  return ctx ?? ({} as unknown as InsightFilterContextType);
};
